import { useEffect, useState } from "react";

export default function WallStreetTitle() {
  const [now, setNow] = useState(new Date());
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const markets = [
    { name: "NYSE / NASDAQ (EEUU)", timeZone: "America/New_York", start: 9.5, end: 16, newsUrl: "https://www.investing.com/indices/us-30" },
    { name: "TSX (Canadá)", timeZone: "America/Toronto", start: 9.5, end: 16, newsUrl: "https://www.investing.com/indices/s-p-tsx-composite" },
    { name: "LSE (Londres)", timeZone: "Europe/London", start: 8, end: 16.5, newsUrl: "https://www.investing.com/indices/uk-100" },
    { name: "Europa (Fráncfort, París, Milán)", timeZone: "Europe/Berlin", start: 9, end: 17.5, newsUrl: "https://www.investing.com/indices/germany-30" },
    { name: "BYMA (Argentina)", timeZone: "America/Argentina/Buenos_Aires", start: 11, end: 17, newsUrl: "https://es.investing.com/equities/bolsas-y-mercados-argentinos-sa" },
    { name: "B3 (Brasil)", timeZone: "America/Sao_Paulo", start: 10, end: 17, newsUrl: "https://www.investing.com/indices/bovespa" },
    { name: "Tokyo (JPX)", timeZone: "Asia/Tokyo", start: 9, end: 15, newsUrl: "https://www.investing.com/indices/japan-ni225" },
    { name: "Shanghai (SSE)", timeZone: "Asia/Shanghai", start: 9.5, end: 15, newsUrl: "https://www.investing.com/indices/shanghai-composite" },
    { name: "Hong Kong (HKEX)", timeZone: "Asia/Hong_Kong", start: 9.5, end: 16, newsUrl: "https://www.investing.com/indices/hang-sen-40" },
    { name: "NSE (India)", timeZone: "Asia/Kolkata", start: 9.25, end: 15.5, newsUrl: "https://www.investing.com/indices/s-p-cnx-nifty" },
    { name: "ASX (Australia)", timeZone: "Australia/Sydney", start: 10, end: 16, newsUrl: "https://www.investing.com/indices/aus-200" },
    { name: "Commodities (Oro, Petróleo)", timeZone: "UTC", start: 1, end: 22, newsUrl: "https://www.investing.com/commodities/" },
  ];

  const getMarketLocalDate = (date, timeZone) => {
    return new Date(date.toLocaleString("en-US", { timeZone }));
  };

  const formatTime = (hoursDecimal) => {
    const totalSeconds = Math.floor(hoursDecimal * 3600);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const marketStatus = markets.map((m) => {
    const localDate = getMarketLocalDate(now, m.timeZone);
    const day = localDate.getDay();
    const isWeekend = day === 0 || day === 6;

    const localHourDecimal =
      localDate.getHours() +
      localDate.getMinutes() / 60 +
      localDate.getSeconds() / 3600;

    let status = "CERRADO";
    let color = "text-slate-400";
    let countdown = "";

    if (!isWeekend) {
      const isOpen =
        localHourDecimal >= m.start && localHourDecimal < m.end;

      if (isOpen) {
        status = "ABIERTO";
        color = "text-emerald-400";
        const hoursLeft = m.end - localHourDecimal;
        countdown = `Cierra en ${formatTime(hoursLeft)}`;
      } else {
        let hoursUntilOpen;
        if (localHourDecimal < m.start) {
          hoursUntilOpen = m.start - localHourDecimal;
        } else {
          hoursUntilOpen = 24 - localHourDecimal + m.start;
        }
        countdown = `Abre en ${formatTime(hoursUntilOpen)}`;
      }
    }

    const localTime = localDate.toLocaleTimeString("en-GB", {
      hour12: false,
    });

    return { ...m, status, color, localTime, countdown };
  });

  const utcTime = now.toUTCString().split(" ")[4];

  return (
    <section className="w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      
      <div className="w-full max-w-7xl mx-auto font-mono rounded-3xl shadow-2xl border border-slate-700 p-6 sm:p-8 lg:p-10">

        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-[0.15em] uppercase text-emerald-400 mb-10 text-center">
          Terminal Financiera
          <span className="block mt-3 text-xs tracking-[0.4em] text-slate-500 font-semibold">
            V1.3
          </span>
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 sm:gap-8 mb-10">
          {marketStatus.map((m, i) => (
            <div
              key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className="relative flex flex-col items-center p-4 bg-slate-900/80 rounded-2xl border border-slate-700 shadow-lg hover:scale-105 transition-transform duration-300 overflow-hidden"
            >
              <span className="text-xs md:text-sm text-slate-400 uppercase tracking-widest text-center">
                {m.name}
              </span>

              <span className={`text-sm md:text-base uppercase font-bold mt-2 ${m.color}`}>
                {m.status}
              </span>

              <span className="text-xs text-slate-400 mt-1">
                {m.localTime}
              </span>

              {m.countdown && (
                <span className="text-xs mt-2 text-sky-400 font-semibold text-center">
                  {m.countdown}
                </span>
              )}

              {hovered === i && m.newsUrl && (
                <div className="absolute inset-0 bg-slate-950/95 backdrop-blur-sm flex flex-col justify-center items-center text-center p-4 transition-all duration-300">
                  <h3 className="text-emerald-400 font-bold text-sm mb-3">
                    Noticias {m.name}
                  </h3>

                  <a
                    href={m.newsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs bg-sky-600 hover:bg-sky-500 px-3 py-1 rounded-lg transition"
                  >
                    Ver en Investing
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-col items-center gap-4 text-slate-400 uppercase tracking-[0.25em] text-center">
          <span className="text-lg sm:text-xl font-bold text-sky-300">
            HORA UTC: {utcTime}
          </span>

          {(now.getUTCDay() === 0 || now.getUTCDay() === 6) && (
            <span className="text-red-400 text-sm tracking-widest">
              FIN DE SEMANA GLOBAL
            </span>
          )}
        </div>

      </div>
    </section>
  );
}